<template>
    <div class="notifications-page">
      <div v-if="loading" class="loading">
        <LoadingSpinner />
      </div>
      
      <div v-else-if="filteredNotifications.length === 0" class="empty-state">
        <i class="fas fa-bell-slash"></i>
        <p>No notifications</p>
      </div>
      
      <div v-else class="notifications-list">
        <!-- Email Confirmation Alert -->
        <div v-if="shouldShowEmailConfirmation" class="email-confirmation-alert">
          <div class="alert-content">
            <i class="fas fa-envelope"></i>
            <p>Please confirm your email address to fully activate your account. The confirmation email may be in your spam folder.</p>
            <button 
              v-if="!emailSentSuccessfully" 
              @click="resendConfirmationEmail" 
              class="resend-email-btn" 
              :disabled="resendingEmail">
              {{ buttonText }}
            </button>
            <span v-else class="email-sent-message">Confirmation email sent!</span>
          </div>
        </div>
        
        <div 
          v-for="notification in filteredNotifications" 
          :key="notification.id" 
          class="notification-item"
          :class="{ 'unread': !notification.is_read, 'read': notification.is_read }"
          @click="handleNotificationClick(notification)"
        >
          <div class="notification-indicator" v-if="!notification.is_read"></div>
          <div class="user-avatar" v-if="notification.content_object && notification.content_object.user">
            <img 
              :src="notification.content_object.user.profile_pic || '/default-avatar.png'" 
              :alt="notification.content_object.user.username" 
              class="profile-pic"
            />
          </div>
          <div class="user-avatar" v-else-if="hasCurrencySymbol(notification.message)">
            <div class="currency-icon">
              <i class="fas fa-dollar-sign"></i>
            </div>
          </div>
          <div class="notification-content">
            <p class="notification-message">{{ notification.message }}</p>
            <p class="notification-preview" v-if="notification.content_object && notification.content_object.text">
              {{ truncateText(notification.content_object.text, 75) }}
            </p>
            <span class="notification-time">{{ formatTime(notification.created_at) }}</span>
          </div>
        </div>
        
        <div v-if="hasMoreNotifications" class="load-more">
          <button @click="loadMoreNotifications" :disabled="loadingMore">
            {{ loadingMore ? 'Loading...' : 'Load More' }}
          </button>
        </div>
        
        <!-- Profile Update Suggestion -->
        <div v-if="shouldShowProfileUpdate" class="profile-update-suggestion">
          <div class="suggestion-content">
            <i class="fas fa-user-edit"></i>
            <p>Complete your profile to connect with more skiers</p>
            <button @click="goToProfileEdit" class="update-profile-btn">
              Update your Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  import eventBus from '@/utils/eventBus';
  
  export default {
    name: 'NotificationsPage',
    components: {
      LoadingSpinner
    },
    data() {
      return {
        notifications: [],
        loading: true,
        loadingMore: false,
        offset: 0,
        limit: 20,
        hasMoreNotifications: false,
        userProfile: null,
        profileLoading: true,
        resendingEmail: false,
        emailSentSuccessfully: false
      };
    },
    created() {
      this.fetchNotifications();
      this.fetchUserProfile();
    },
    methods: {
      async fetchNotifications() {
        const token = localStorage.getItem('access_token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        try {
          this.loading = true;
          const response = await axios.get('https://api.6buoys.com/api/notifications/', {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              limit: this.limit,
              offset: this.offset
            }
          });

          
          this.notifications = response.data;
          this.hasMoreNotifications = response.data.length === this.limit;
          this.loading = false;
          
          // Check if there are more than 5 unread notifications
          const unreadCount = this.notifications.filter(n => !n.is_read).length;
          if (unreadCount > 5) {
            this.markAllAsRead();
          }
        } catch (error) {
          console.error('Error fetching notifications:', error);
          this.loading = false;
        }
      },
      
      async loadMoreNotifications() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          this.loadingMore = true;
          this.offset += this.limit;
          
          const response = await axios.get('https://api.6buoys.com/api/notifications/', {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              limit: this.limit,
              offset: this.offset
            }
          });
          
          this.notifications = [...this.notifications, ...response.data];
          this.hasMoreNotifications = response.data.length === this.limit;
          this.loadingMore = false;
        } catch (error) {
          console.error('Error loading more notifications:', error);
          this.loadingMore = false;
        }
      },
      
      async handleNotificationClick(notification) {
        // Mark as read if it's unread
        if (!notification.is_read) {
          await this.markAsRead(notification.id);
          
          // Use the eventBus instead of $root.$emit
          eventBus.emit('notification-read');
        }
        
        // Navigate to the linked content if available
        if (notification.link) {
          this.$router.push(notification.link);
        }
      },
      
      async markAsRead(notificationId) {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          // Updated endpoint to match the backend URL structure
          await axios.post(`https://api.6buoys.com/api/notifications/mark-read/${notificationId}/`, {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          // Update the local state - Vue 3 way
          const index = this.notifications.findIndex(n => n.id === notificationId);
          if (index !== -1) {
            // Create a new array with the updated notification
            this.notifications = [
              ...this.notifications.slice(0, index),
              { ...this.notifications[index], is_read: true },
              ...this.notifications.slice(index + 1)
            ];
          }
        } catch (error) {
          console.error('Error marking notification as read:', error);
        }
      },
      
      async markAllAsRead() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          await axios.post('https://api.6buoys.com/api/notifications/mark-all-read/', {}, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          // Update local state to mark all notifications as read
          this.notifications = this.notifications.map(notification => ({
            ...notification,
            is_read: true
          }));
          
          // Emit event to update notification counts elsewhere in the app
          eventBus.emit('notification-read');
        } catch (error) {
          console.error('Error marking all notifications as read:', error);
        }
      },
      
      formatTime(timestamp) {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);
        
        if (diffInSeconds < 60) {
          return 'just now';
        } else if (diffInSeconds < 3600) {
          const minutes = Math.floor(diffInSeconds / 60);
          return `${minutes}m ago`;
        } else if (diffInSeconds < 86400) {
          const hours = Math.floor(diffInSeconds / 3600);
          return `${hours}h ago`;
        } else if (diffInSeconds < 604800) {
          const days = Math.floor(diffInSeconds / 86400);
          return `${days}d ago`;
        } else {
          return date.toLocaleDateString();
        }
      },
      
      truncateText(text, maxLength) {
        // Increase maxLength by 20%
        const adjustedMaxLength = Math.floor(maxLength);
        if (text.length <= adjustedMaxLength) {
          return text;
        }
        return text.substring(0, adjustedMaxLength) + '...';
      },
      
      hasCurrencySymbol(text) {
        if (!text) return false;
        return text.includes('$') || text.includes('€');
      },
      
      async fetchUserProfile() {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        try {
          const response = await axios.get('https://api.6buoys.com/api/profile/', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          
          this.userProfile = response.data;
          this.profileLoading = false;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          this.profileLoading = false;
        }
      },
      
      goToProfileEdit() {
        this.$router.push('/edit-profile');
      },
      
      async resendConfirmationEmail() {
        const token = localStorage.getItem('access_token');
        if (!token || !this.userProfile || !this.userProfile.email) return;
        
        try {
          this.resendingEmail = true;
          
          await axios.post('https://api.6buoys.com/api/resend-confirmation-email/', 
            { email: this.userProfile.email },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );
          
          // Set a flag to indicate email was sent successfully
          this.emailSentSuccessfully = true;
          
        } catch (e) {
          console.error('Error sending confirmation email:', e);
          alert('Failed to send confirmation email. Please try again later.');
        } finally {
          this.resendingEmail = false;
        }
      }
    },
    computed: {
      filteredNotifications() {
        // Always return all notifications regardless of filter
        return this.notifications;
      },
      
      shouldShowProfileUpdate() {
        if (this.profileLoading || !this.userProfile) return false;
        
        // Don't show profile update suggestion if email is not confirmed
        if (this.userProfile.confirmed_via_email === false) {
          return false;
        }
        
        // Check if bio is empty or the default "no bio..."
        const hasRealBio = this.userProfile.bio && 
                          this.userProfile.bio.trim().length > 0 && 
                          this.userProfile.bio !== "no bio...";
        
        // Check if using default avatar (contains "avatar.png" in the URL)
        const hasCustomAvatar = this.userProfile.avatar && 
                               !this.userProfile.avatar.includes('avatar.png');
        
        // Check if ski, lake, and PB are filled in
        const hasSkiInfo = this.userProfile.ski && this.userProfile.ski.trim().length > 0;
        const hasLakeInfo = this.userProfile.lake && this.userProfile.lake.trim().length > 0;
        const hasPBInfo = this.userProfile.PB && this.userProfile.PB.trim().length > 0;
        
        // For debugging
        console.log('Profile check:', {
          bio: this.userProfile.bio,
          avatar: this.userProfile.avatar,
          ski: this.userProfile.ski,
          lake: this.userProfile.lake,
          PB: this.userProfile.PB,
          hasRealBio,
          hasCustomAvatar,
          hasSkiInfo,
          hasLakeInfo,
          hasPBInfo
        });
        
        // Show update suggestion if any of these fields are missing
        return !hasRealBio || !hasCustomAvatar || !hasSkiInfo || !hasLakeInfo || !hasPBInfo;
      },
      
      shouldShowEmailConfirmation() {
        return this.userProfile && this.userProfile.confirmed_via_email === false;
      },
      
      buttonText() {
        if (this.resendingEmail) {
          return 'Sending...';
        } else if (this.emailSentSuccessfully) {
          return 'Confirmation email sent!';
        } else {
          return 'Resend Confirmation Email';
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .notifications-page {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .loading, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
    color: #8899a6;
  }
  
  .empty-state i {
    font-size: 2rem;
    margin-bottom: 15px;
  }
  
  .notifications-list {
    border-radius: 10px;
    overflow: hidden;
  }
  
  .notification-item {
    display: flex;
    padding: 15px;
    border-bottom: 1px solid #38444d;
    cursor: pointer;
    transition: background-color 0.2s ease;
    position: relative;
    align-items: center;
  }
  
  .notification-item:hover {
    background-color: #22303c;
  }
  
  .notification-item.unread {
    background-color: rgba(29, 161, 242, 0.1);
  }
  
  .notification-item.read {
    background-color: transparent;
    opacity: 0.8;
  }
  
  .notification-indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #1da1f2;
    position: absolute;
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
  }
  
  .user-avatar {
    margin-right: 10px;
    margin-left: 10px;
    display: flex;
    align-items: center;
  }
  
  .profile-pic {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #1da1f2;
    object-fit: cover;
  }
  
  .currency-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid #1da1f2;
    background-color: #1da1f2;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .currency-icon i {
    color: white;
    font-size: 1.2rem;
  }
  
  .notification-content {
    flex: 1;
    padding-left: 5px;
    text-align: left;
  }
  
  .notification-message {
    margin: 0 0 5px 0;
    color: #ffffff;
    font-size: 0.95rem;
  }
  
  .notification-preview {
    margin: 0 0 5px 0;
    color: #8899a6;
    font-size: 0.85rem;
    font-style: italic;
  }
  
  .notification-time {
    font-size: 0.8rem;
    color: #8899a6;
  }
  
  .load-more {
    display: flex;
    justify-content: center;
    padding: 15px;
  }
  
  .load-more button {
    background-color: transparent;
    border: 1px solid #1da1f2;
    color: #1da1f2;
    padding: 8px 16px;
    border-radius: 20px;
    cursor: pointer;
    font-weight: bold;
    transition: background-color 0.2s ease;
  }
  
  .load-more button:hover:not(:disabled) {
    background-color: rgba(29, 161, 242, 0.1);
  }
  
  .load-more button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  /* Profile Update Suggestion Styles */
  .profile-update-suggestion {
    margin-top: 20px;
    padding: 20px;
    border-radius: 10px;
    background-color: rgba(29, 161, 242, 0.1);
    border: 1px solid #38444d;
  }
  
  .suggestion-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .suggestion-content i {
    font-size: 2rem;
    color: #1da1f2;
    margin-bottom: 15px;
  }
  
  .suggestion-content p {
    margin-bottom: 15px;
    color: #ffffff;
  }
  
  .update-profile-btn {
    background-color: #1da1f2;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 20px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .update-profile-btn:hover {
    background-color: #0c8ddb;
  }
  
  /* Email Confirmation Alert Styles */
  .email-confirmation-alert {
    margin-bottom: 20px;
    padding: 20px;
    border-radius: 10px;
    background-color: rgba(255, 193, 7, 0.1);
    border: 1px solid #ffc107;
  }
  
  .alert-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .alert-content i {
    font-size: 2rem;
    color: #ffc107;
    margin-bottom: 15px;
  }
  
  .alert-content p {
    margin-bottom: 15px;
    color: #ffffff;
  }
  
  .resend-email-btn {
    background-color: #ffc107;
    color: #333;
    border: none;
    padding: 10px 20px;
    border-radius: 20px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .resend-email-btn:hover:not(:disabled) {
    background-color: #e0a800;
  }
  
  .resend-email-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
  
  .email-sent-message {
    margin-left: 10px;
    color: #1da1f2;
    font-weight: bold;
  }
  </style>